import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import { stripOuterParagraph } from '@eneco/cms-types';
import RichText from '@eneco/components/RichText/RichText';
import { useAcquisitionGetProductTariffs } from '@eneco/dc/hooks/sales';
import { getMarketingProductCodes, getProductTariffsProducts, getTariffValues } from '@eneco/dc/modelContract/tariffs';
import useDC from '@eneco/dc/useDC';
import { useFormatter } from '@eneco/i18n';
import { NotificationBox, Skeleton, Stack, Stretch, Table as SparkyTable, Text } from '@eneco/sparky';

import type { ModelContractTableContent } from './model-contract-table-content.types';
import { isSanityTable, tableRows } from './table-rows';

/** Fixed costs (e.g. `{vasteLeveringskostenGasPerMaandVast}`) are monthly prices, everything else is a per-unit tariff. */
const isFixedCostPlaceholder = (placeholder: string) => placeholder.replace(/^{/, '').startsWith('vaste');

/**
 * `ModelContractTable` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors `libs/sitecore/components/content/src/ModelContractTable/ModelContractTable.tsx`: a table
 * whose first column is author copy and whose remaining cells are placeholder tokens swapped for live
 * tariffs from the Digital Core. Unblocked by the `lib:i18n` widening — every number in it is a
 * formatted euro amount, and `currency.euro` vs `currency.euroFiveDecimals` is chosen per cell.
 *
 * Rows arrive in Sitecore's Table resolver shape or, on Sanity, nested in `items`; `tableRows` reads both. A
 * Sanity cell is rich text and so arrives wrapped in a paragraph, which is unwrapped before a header is shown
 * as text or a placeholder is looked up, as legacy reads Sitecore's bare value. Sitecore's values are used as
 * they come, as legacy does.
 */
const ModelContractTable: FC = () => {
  const content = useContent<ModelContractTableContent>();
  const { label } = useDC();
  const { currency } = useFormatter();

  const { headerRows, rows } = tableRows(content);
  const unwrap = isSanityTable(content);

  const codes = getMarketingProductCodes(label);
  // Only body cells look tariffs up, so the request is worth making only when there are rows. `null`
  // is the hook's "do not fetch", and a block that never fetches is never loading — without this a
  // title-only block posts every tariff product to the Digital Core and shows a 480px skeleton first, for an
  // answer nothing on screen can use.
  const wantsTariffs = Boolean(rows?.length);
  const { data, isLoading } = useAcquisitionGetProductTariffs(
    codes && wantsTariffs ? { products: getProductTariffsProducts(codes) } : null,
  );

  const tariffs = getTariffValues(codes, data?.productTariffs);

  if (isLoading) {
    return (
      <Stack direction="row" gap="2">
        <Stretch>
          <Skeleton height="480px" />
        </Stretch>
      </Stack>
    );
  }

  if (!headerRows?.length && !rows?.length) {
    return (
      <>
        {content.title && <Text weight="bold">{content.title}</Text>}
        {process.env.NODE_ENV !== 'production' && (
          <NotificationBox
            isAlert={false}
            title="Model contract table rows not migrated"
            text="This block has no rows."
          />
        )}
      </>
    );
  }

  return (
    <SparkyTable title={content.title}>
      {headerRows?.[0] && (
        <SparkyTable.Header>
          {(headerRows[0].columns ?? []).map((column, index) => (
            <HeaderCell key={index} content={column.content} unwrap={unwrap} />
          ))}
        </SparkyTable.Header>
      )}

      {rows?.map((row, rowIndex) => (
        <SparkyTable.Row key={rowIndex}>
          {(row.columns ?? []).map((column, columnIndex) => (
            <BodyCell
              key={columnIndex}
              content={column.content}
              // The first column is author copy; every other cell is a tariff placeholder.
              isCopy={columnIndex === 0}
              unwrap={unwrap}
              tariffs={tariffs}
              currency={currency}
            />
          ))}
        </SparkyTable.Row>
      ))}
    </SparkyTable>
  );
};

/** `unwrap`: a Sanity cell, whose rich text arrives wrapped in a paragraph. */
const HeaderCell: FC<{ content: unknown; unwrap: boolean }> = ({ content, unwrap }) => {
  const { adapter } = useCms();
  const html = adapter.toRichText(content);
  return <Text>{(unwrap ? stripOuterParagraph(html) : html) ?? ''}</Text>;
};

type CurrencyFormatter = ReturnType<typeof useFormatter>['currency'];

const BodyCell: FC<{
  content: unknown;
  isCopy: boolean;
  unwrap: boolean;
  tariffs: Record<string, number | undefined>;
  currency: CurrencyFormatter;
}> = ({ content, isCopy, unwrap, tariffs, currency }) => {
  const { adapter } = useCms();
  const html = adapter.toRichText(content) ?? '';

  if (isCopy) return <RichText html={html} />;

  const placeholder = (unwrap ? stripOuterParagraph(html) : html) ?? '';
  const tariff = tariffs[placeholder];
  const formatTariff = isFixedCostPlaceholder(placeholder) ? currency.euro : currency.euroFiveDecimals;

  return <RichText html={tariff === undefined ? '--' : formatTariff(tariff)} />;
};

export default ModelContractTable;
